/* eslint-disable jsx-a11y/aria-role */
import {
    DragEventHandler,
    MouseEventHandler,
    FocusEventHandler,
    KeyboardEventHandler,
    MutableRefObject,
    useState,
    useEffect,
    useRef,
    useLayoutEffect,
    KeyboardEvent,
    DragEvent
} from "react";

import { styled } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { token } from "@atlaskit/tokens";
import VisuallyHidden from "@atlaskit/visually-hidden";

import usePrevious from "../hooks/use-previous.hook";
import { messages } from "../messages";
import { Actions } from "../state";

/**
 * The default roadmap bar width.
 */
const roadmapBarWidth = 206;
const maxRoadmapBarSize = 616;

/**
 * The minimum roadmap bar width.
 */
const minimumRoadmapBarWidth = 80;

/**
 * The custom adjust roadmap bar event.
 */
const CUSTOM_ADJUST_ROADMAP_BAR_EVENT = "CUSTOM_ADJUST_ROADMAP_BAR";

/**
 * Key codes for keyboard event handling
 */
const SPACEBAR_KEY = " ";
const ENTER_KEY = "enter";
const LEFT_ARROW_KEY = "arrowleft";
const RIGHT_ARROW_KEY = "arrowright";
const A_KEY = "a";
const D_KEY = "d";

/**
 * Gets the jsw interactive roadmap container offset.
 */
const getJswInteractiveRoadmapContainerOffset = () => {
    const element = document.getElementById(
        "main-content-wrapper-jsw-interactive-roadmap"
    );

    const { left: offsetLeft, width: offsetWidth } =
        element?.getBoundingClientRect() ?? {
            left: 0,
            width: 0
        };

    return {
        offsetLeft,
        offsetWidth
    };
};

/**
 * A JSX component for the roadmap bar.
 * @param param0
 * @returns A roadmap column JSX component.
 */
export const RoadmapBar = ({
    epicId,
    storyId,
    color,
    lineColor,
    onClickProjectTimeline,
    onAdjustProjectTimeline,
    canAdjust,
    isDemoComplete,
    width = 80,
    start = 2,
    isVisible = true,
    barHeight = 20,
    childrenIds = []
}: RoadmapBarPropsType) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();
    const barRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
    const [showAdjustmentLines, setShowAdjustmentLines] = useState(false);
    const [mouseDown, setMouseDown] = useState(false);
    const [barWidth, setBarWidth] = useState(width);
    const [barWidthOnDrag, setBarWidthOnDrag] = useState(barWidth);
    const [marginLeft, setMarginLeft] = useState(
        Math.max((start - 2) * roadmapBarWidth, 1)
    );
    const [initialBarOffset, setInitialBarOffset] = useState(0);
    const [leftAdjustment, setLeftAdjustment] = useState(marginLeft);
    const [isKeyboardEditMode, setKeyboardEditMode] = useState(false);
    const ariaLiveContainerId = "roadmap-bar-aria-live";
    const prevLeftAdjustment = usePrevious(leftAdjustment) ?? 0;

    const hideVertivalLinesOnRoadmapBar = () => {
        if (!mouseDown) {
            setShowAdjustmentLines(false);
        }

        setMouseDown(false);
    };

    // if there is no story id, this is an epic. listen to children changes to calculate bar width.
    useLayoutEffect(() => {
        if (!!storyId) {
            return;
        }

        if (!childrenIds || childrenIds.length === 0) {
            console.info("This epic has no children/stories.");
            return;
        }

        const adjustEpicLength = () => {
            let minOffset = Number.MAX_SAFE_INTEGER;
            let longestRoadmapBarWidth = Number.MIN_SAFE_INTEGER;

            // calculate min offset
            for (const childId of childrenIds) {
                const roadmapBar = document.getElementById(childId)
                    ?.firstElementChild as HTMLElement;

                if (!roadmapBar) {
                    return;
                }

                minOffset = Math.max(
                    Math.min(minOffset, roadmapBar.offsetLeft - 290),
                    1
                );
            }

            // calculate longest roadmapbar width
            for (const childId of childrenIds) {
                const roadmapBar = document.getElementById(childId)
                    ?.firstElementChild as HTMLElement;

                if (!roadmapBar) {
                    return;
                }

                longestRoadmapBarWidth = Math.max(
                    longestRoadmapBarWidth,
                    roadmapBar.offsetLeft +
                        roadmapBar.offsetWidth -
                        290 -
                        minOffset
                );
            }

            if (barRef.current) {
                barRef.current.style.width = `${longestRoadmapBarWidth}px`;
            }

            setMarginLeft(minOffset);
            setBarWidth(longestRoadmapBarWidth);
        };

        addEventListener(CUSTOM_ADJUST_ROADMAP_BAR_EVENT, adjustEpicLength);
        adjustEpicLength();
    }, [childrenIds]);

    useEffect(() => {
        // if a11y mode
        if (isKeyboardEditMode && barRef.current) {
            barRef.current.style.width = `${barWidth}px`;
            setMarginLeft(leftAdjustment);
            return;
        }

        if (barRef.current) {
            let width = barWidthOnDrag;

            if (leftAdjustment > prevLeftAdjustment) {
                width =
                    barWidthOnDrag -
                    Math.abs(leftAdjustment - prevLeftAdjustment);
            } else {
                width =
                    barWidthOnDrag +
                    Math.abs(leftAdjustment - prevLeftAdjustment);
            }

            // width not allowed
            if (width < minimumRoadmapBarWidth || isNaN(width)) {
                return;
            }

            barRef.current.style.width = `${width}px`;
            setBarWidth(width);
            setBarWidthOnDrag(width);
            setMarginLeft(leftAdjustment);
        }
    }, [leftAdjustment]);

    useEffect(() => {
        if (barRef.current) {
            setInitialBarOffset(barRef.current.offsetLeft - leftAdjustment);
        }
    }, [barRef]);

    const opacity = isVisible ? 1 : 0;
    const hoverOpacity = isVisible ? 1 : 0.5;

    let onClick: MouseEventHandler<HTMLDivElement> | undefined = undefined;
    let onMouseUp: MouseEventHandler<HTMLDivElement> | undefined = undefined;
    let onMouseDown: MouseEventHandler<HTMLDivElement> | undefined = undefined;
    let onMouseEnter: MouseEventHandler<HTMLDivElement> | undefined = undefined;
    let onMouseLeave: MouseEventHandler<HTMLDivElement> | undefined = undefined;
    let onDragStartLeft: DragEventHandler<HTMLDivElement> | undefined =
        undefined;
    let onDragStartRight: DragEventHandler<HTMLDivElement> | undefined =
        undefined;

    let onDragLeft: DragEventHandler<HTMLDivElement> | undefined = undefined;
    let onDragLeftEnd: DragEventHandler<HTMLDivElement> | undefined = undefined;
    let onDragRight: DragEventHandler<HTMLDivElement> | undefined = undefined;
    let onDragRightEnd: DragEventHandler<HTMLDivElement> | undefined =
        undefined;

    let onFocus: FocusEventHandler<HTMLDivElement> | undefined = undefined;
    let onBlur: FocusEventHandler<HTMLDivElement> | undefined = undefined;

    let onKeyDownLeft: KeyboardEventHandler<HTMLDivElement> | undefined =
        undefined;
    let onKeyDownRight: KeyboardEventHandler<HTMLDivElement> | undefined =
        undefined;

    if (onClickProjectTimeline && !isVisible) {
        onClick = () => onClickProjectTimeline();
    }

    const dispatchWindowEventProjectTimelineAdjusted = () => {
        // dispatch window event to listen to changes
        const adjustRoadmapEvent = new CustomEvent(
            CUSTOM_ADJUST_ROADMAP_BAR_EVENT,
            {
                detail: {
                    epicId
                }
            }
        );

        dispatchEvent(adjustRoadmapEvent);
    };

    if (canAdjust) {
        onMouseDown = () => setMouseDown(true);
        onMouseUp = () => setMouseDown(false);
        onFocus = () => setShowAdjustmentLines(true);
        onBlur = () => setShowAdjustmentLines(false);
        onDragStartLeft = ({ dataTransfer }: DragEvent<HTMLDivElement>) => {
            setMouseDown(true);
            setShowAdjustmentLines(true);
            setBarWidthOnDrag(barWidth);

            // hide ghost image
            const blankCanvas: HTMLCanvasElement =
                document.createElement("canvas");
            blankCanvas.style.opacity = "0";
            dataTransfer.setDragImage(blankCanvas, 0, 0);
            document.body.appendChild(blankCanvas);
        };

        onDragStartRight = ({ dataTransfer }: DragEvent<HTMLDivElement>) => {
            setMouseDown(true);
            setShowAdjustmentLines(true);
            setBarWidthOnDrag(barWidth);

            // hide ghost image
            const blankCanvas: HTMLCanvasElement =
                document.createElement("canvas");
            blankCanvas.style.opacity = "0";
            dataTransfer.setDragImage(blankCanvas, 0, 0);
            document.body.appendChild(blankCanvas);

            // fire window event for spotlight component.
            const windowEvent = new Event(Actions.ADJUST_PROJECT_TIMELINE);

            createAnalyticsEvent({
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: "interactiveRoadmapAdjustProjectTimeline",
                eventType: "ui"
            }).fire();

            dispatchEvent(windowEvent);
        };

        onDragRight = ({ clientX }) => {
            if (clientX <= 0 || !isVisible) {
                return;
            }

            const { offsetLeft, offsetWidth } =
                getJswInteractiveRoadmapContainerOffset();
            const maxAdjustment = Math.min(clientX, offsetLeft + offsetWidth);
            const barOffset =
                initialBarOffset + leftAdjustment + offsetLeft + 12;

            let width = Math.max(
                isDemoComplete ? minimumRoadmapBarWidth : roadmapBarWidth,
                maxAdjustment - barOffset
            );

            width = Math.floor(width / 10) * 10;

            // skip update if bar width did not change
            if (barWidth === width) {
                return;
            }

            setBarWidth(width);

            if (barRef.current) {
                barRef.current.style.width = `${width}px`;
            }

            dispatchWindowEventProjectTimelineAdjusted();
        };

        onDragLeft = ({ clientX }) => {
            if (!isDemoComplete) {
                return;
            }

            if (clientX <= 0 || !isVisible) {
                return;
            }

            const { offsetLeft } = getJswInteractiveRoadmapContainerOffset();
            const updatedClientX = Math.floor(clientX / 10) * 10;
            const barOffset = initialBarOffset + offsetLeft + 15;
            const updatedLeftAdjustment = updatedClientX - (barOffset ?? 0);

            // check if width is allowed
            let width = 0;
            if (updatedLeftAdjustment > prevLeftAdjustment) {
                width =
                    barWidthOnDrag -
                    Math.abs(updatedLeftAdjustment - prevLeftAdjustment);
            } else {
                width =
                    barWidthOnDrag +
                    Math.abs(updatedLeftAdjustment - prevLeftAdjustment);
            }

            // adjustment was passed roadmap bar min width
            if (width < minimumRoadmapBarWidth) {
                return;
            }

            setLeftAdjustment(Math.max(updatedLeftAdjustment, 1));
            dispatchWindowEventProjectTimelineAdjusted();
        };

        onDragRightEnd = () => {
            // the user did not leave the first column.
            if (barRef.current && barRef.current.offsetWidth < 205) {
                setShowAdjustmentLines(false);

                // dispatch event for the spotlight so instructions can be viewed again.
                const windowEvent = new Event(
                    `${Actions.ADJUST_PROJECT_TIMELINE}_REWIND`
                );

                createAnalyticsEvent({
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId: "interactiveRoadmapAdjustProjectTimeline",
                    eventType: "ui"
                }).fire();

                dispatchEvent(windowEvent);

                return;
            }

            setShowAdjustmentLines(false);
            if (onAdjustProjectTimeline) {
                onAdjustProjectTimeline();
            }
            hideVertivalLinesOnRoadmapBar();
        };

        onDragLeftEnd = () => {
            if (barRef.current) {
                setBarWidth(barRef.current.offsetWidth);
            }
            hideVertivalLinesOnRoadmapBar();
        };

        onMouseEnter = () => setShowAdjustmentLines(true);
        onMouseLeave = hideVertivalLinesOnRoadmapBar;

        // eslint-disable-next-line complexity
        onKeyDownRight = (event: KeyboardEvent<HTMLDivElement>) => {
            switch (event.key.toLowerCase()) {
                case ENTER_KEY:
                case SPACEBAR_KEY:
                    event.preventDefault();

                    if (!isKeyboardEditMode) {
                        // enter keyboard edit mode; message box disappear
                        const windowEvent = new Event(
                            Actions.ADJUST_PROJECT_TIMELINE
                        );

                        createAnalyticsEvent({
                            action: "clicked",
                            actionSubject: "button",
                            actionSubjectId:
                                "interactiveRoadmapAdjustProjectTimeline",
                            eventType: "ui"
                        }).fire();

                        dispatchEvent(windowEvent);

                        updateAriaLiveText(
                            intl.formatMessage(
                                messages.ariaLabelSpacebarToEditKeyPress
                            )
                        );
                    } else {
                        // if bar didn't change size make spotlight appear
                        if (
                            barRef.current &&
                            barRef.current.offsetWidth < 206
                        ) {
                            const windowEvent = new Event(
                                `${Actions.ADJUST_PROJECT_TIMELINE}_REWIND`
                            );

                            createAnalyticsEvent({
                                action: "clicked",
                                actionSubject: "button",
                                actionSubjectId:
                                    "interactiveRoadmapAdjustProjectTimeline",
                                eventType: "ui"
                            }).fire();

                            dispatchEvent(windowEvent);
                        }
                        // bar did change transition to next step
                        else {
                            if (onAdjustProjectTimeline) {
                                onAdjustProjectTimeline();
                            }
                            dispatchWindowEventProjectTimelineAdjusted();
                        }

                        updateAriaLiveText(
                            intl.formatMessage(
                                messages.ariaLabelSpacebarToPlaceKeyPress
                            )
                        );
                    }
                    setKeyboardEditMode(!isKeyboardEditMode);
                    return;
                case A_KEY:
                case LEFT_ARROW_KEY:
                    // if not in edit mode
                    if (!isKeyboardEditMode) {
                        return;
                    }
                    event.preventDefault();

                    // if roadmap bar is already min size
                    if (
                        isDemoComplete &&
                        barWidth - 10 < minimumRoadmapBarWidth
                    ) {
                        return;
                    } // if roadmap bar is standard size
                    else if (
                        !isDemoComplete &&
                        barWidth - 9 < roadmapBarWidth
                    ) {
                        return;
                    }

                    if (barRef.current) {
                        barRef.current.style.width = `${barWidth - 10}px`;
                    }
                    setBarWidth(barWidth - 10);

                    updateAriaLiveText(
                        intl.formatMessage(messages.ariaLabelLeftArrowPress)
                    );

                    return;
                case D_KEY:
                case RIGHT_ARROW_KEY:
                    // if not in edit mode
                    if (!isKeyboardEditMode) {
                        return;
                    }
                    event.preventDefault();

                    // if bar is max size
                    if (barWidth + leftAdjustment + 10 > maxRoadmapBarSize) {
                        return;
                    }

                    if (barRef.current) {
                        barRef.current.style.width = `${barWidth + 10}px`;
                    }
                    setBarWidth(barWidth + 10);

                    updateAriaLiveText(
                        intl.formatMessage(messages.ariaLabelRightArrowPress)
                    );
            }
        };
        onKeyDownLeft = (event: KeyboardEvent<HTMLDivElement>) => {
            // if demo not complete
            if (!isDemoComplete) {
                return;
            }
            switch (event.key.toLowerCase()) {
                case ENTER_KEY:
                case SPACEBAR_KEY:
                    event.preventDefault();

                    if (!isKeyboardEditMode) {
                        updateAriaLiveText(
                            intl.formatMessage(
                                messages.ariaLabelSpacebarToEditKeyPress
                            )
                        );
                    }

                    // on exit keyboard edit mode adjust epic bar
                    if (isKeyboardEditMode) {
                        dispatchWindowEventProjectTimelineAdjusted();
                        updateAriaLiveText(
                            intl.formatMessage(
                                messages.ariaLabelSpacebarToPlaceKeyPress
                            )
                        );
                    }

                    setKeyboardEditMode(!isKeyboardEditMode);
                    return;
                case A_KEY:
                case LEFT_ARROW_KEY:
                    // if not in edit mode
                    if (!isKeyboardEditMode) {
                        return;
                    }
                    event.preventDefault();

                    if (leftAdjustment > 1) {
                        setLeftAdjustment(leftAdjustment - 10);
                        setBarWidth(barWidth + 10);
                        updateAriaLiveText(
                            intl.formatMessage(messages.ariaLabelLeftArrowPress)
                        );
                    }
                    return;
                case D_KEY:
                case RIGHT_ARROW_KEY:
                    // if not in edit mode
                    if (!isKeyboardEditMode) {
                        return;
                    }
                    event.preventDefault();

                    if (barWidth + 10 > minimumRoadmapBarWidth) {
                        setLeftAdjustment(leftAdjustment + 10);
                        setBarWidth(barWidth - 10);
                        updateAriaLiveText(
                            intl.formatMessage(
                                messages.ariaLabelRightArrowPress
                            )
                        );
                    }
            }
        };
    }

    const verticalLineOpacity =
        isVisible && canAdjust && showAdjustmentLines ? "1" : "0";

    const showChildControls = !!(
        isVisible &&
        canAdjust &&
        onClick === undefined
    );

    const updateAriaLiveText = (text: string) => {
        const ariaLiveContainer = document.getElementById(ariaLiveContainerId);
        if (ariaLiveContainer) {
            ariaLiveContainer.innerText = "";
            setTimeout(() => {
                ariaLiveContainer.innerText = text;
            }, 300);
        }
    };

    return (
        <Container
            as={onClick === undefined ? "div" : "button"}
            ref={barRef}
            data-testid="roadmap-bar"
            opacity={opacity}
            barWidth={`${barWidth}px`}
            marginLeft={`${marginLeft}px`}
            hoverOpacity={hoverOpacity}
            barHeight={barHeight}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onClick={onClick}
            color={color}
        >
            {showChildControls && (
                <>
                    <VerticalLineContainer
                        aria-label={intl.formatMessage(
                            messages.ariaLabelLeftVerticalRoadmapBar
                        )}
                        role="input"
                        data-testid="left-vertical-line-container"
                        draggable={canAdjust}
                        onDragStart={onDragStartLeft}
                        onDrag={onDragLeft}
                        onDragEnd={onDragLeftEnd}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onKeyDown={onKeyDownLeft}
                        tabIndex={0}
                    >
                        <VerticalLine
                            opacity={verticalLineOpacity}
                            lineColor={lineColor}
                        />
                    </VerticalLineContainer>

                    <VerticalLineContainer
                        aria-label={intl.formatMessage(
                            messages.ariaLabelRightVerticalRoadmapBar
                        )}
                        role="input"
                        data-testid="right-vertical-line-container"
                        draggable={canAdjust}
                        onDragStart={onDragStartRight}
                        onDrag={onDragRight}
                        onDragEnd={onDragRightEnd}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onKeyDown={onKeyDownRight}
                        tabIndex={0}
                    >
                        <VerticalLine
                            opacity={verticalLineOpacity}
                            lineColor={lineColor}
                        />
                    </VerticalLineContainer>
                </>
            )}
            {/* Elements with the role status have an implicit aria-live value of polite and an implicit aria-atomic value of true. */}

            <VisuallyHidden
                role="status"
                id={ariaLiveContainerId}
                /* eslint-disable-next-line react/no-children-prop */
                children={""}
            />
        </Container>
    );
};

export type RoadmapBarPropsType = {
    epicId?: string;
    storyId?: string;
    width?: number;
    start?: number;
    childrenIds?: string[];
    isDemoComplete: boolean;
    barHeight?: number;
    color?: string;
    lineColor?: string;
    // eslint-disable-next-line @typescript-eslint/ban-types
    onClickProjectTimeline?: Function;
    // eslint-disable-next-line @typescript-eslint/ban-types
    onAdjustProjectTimeline?: Function;
    canAdjust?: boolean;
    isVisible?: boolean;
};

const Container = styled.div<{
    readonly color: string | undefined;
    readonly barHeight: number | undefined;
    readonly opacity: number;
    readonly hoverOpacity: number;
    readonly barWidth: string;
    readonly marginLeft: string;
}>({
    opacity: ({ opacity }) => opacity,
    display: "flex",
    border: "none",
    justifyContent: "space-between",
    alignItems: "center",
    width: ({ barWidth }) => barWidth,
    // lime500 no token has it :/
    backgroundColor: ({ color = "#82B536" }) => color,
    borderRadius: "4px",
    height: ({ barHeight }) => `${barHeight}px`,
    marginLeft: ({ marginLeft }) => marginLeft,
    transition: "transform 1s ease-in",
    ["&:focus, &:hover"]: {
        opacity: ({ hoverOpacity }) => hoverOpacity
    }
});

const VerticalLineContainer = styled.div({
    height: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0)",
    ["&:hover"]: {
        cursor: "ew-resize"
    }
});

const VerticalLine = styled.div<{
    readonly opacity: string;
    readonly lineColor?: string;
}>({
    width: "4px",
    height: "80%",
    borderRadius: "4px",
    backgroundColor: ({
        lineColor = token("color.chart.lime.bolder", "#5B7F24")
    }) => lineColor,
    margin: "0px 5px",
    transition: "all 0.1s ease-out",
    opacity: ({ opacity }) => opacity
});
